import { Link } from "react-router-dom";
import { CalendarClock, ArrowUpRight, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { usePageMeta } from "@/lib/api";

export default function BookPage() {
  usePageMeta("Book a Consultation | SarvaRitam", "Book an ESG consultation with SarvaRitam — scheduling interface ready for Calendly, Google or Microsoft Calendar integration.");
  return (
    <>
      <PageHero
        eyebrow="Book a consultation"
        title={[<>Reserve time with an <span className="serif-it text-gold">ESG advisor.</span></>]}
        intro="A focused, no-obligation conversation about your organisation's ESG situation — where you stand, what's coming, and the clearest next step."
      />
      <section className="bg-offwhite text-charcoal sec" data-testid="book-page">
        <div className="wrap grid lg:grid-cols-2 gap-6 items-stretch">
          <Reveal>
            <div className="h-full border rule-light bg-white/60 p-10 sm:p-12">
              <p className="eyebrow text-forest mb-7">What to expect</p>
              <div className="space-y-7">
                {[
                  ["30 minutes, focused on you", "No slides. We ask about your organisation, sector and priorities, and listen."],
                  ["An honest read on your position", "Where you likely stand on readiness — and what typically matters most at your stage."],
                  ["A clear suggested next step", "A recommendation you can act on, whether or not we work together."],
                ].map(([t, d], i) => (
                  <div key={t} className="flex gap-5">
                    <span className="font-mono text-[12px] text-gold mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h2 className="font-display font-semibold text-lg">{t}</h2>
                      <p className="text-charcoal/60 text-[14px] mt-2 leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10 pt-8 border-t rule-light space-y-2.5">
                {["Confidential by default", "No obligation to engage", "Useful even as a one-off"].map((t) => (
                  <p key={t} className="flex items-center gap-2.5 text-[13.5px] font-semibold text-charcoal/70">
                    <Check size={15} className="text-forest" /> {t}
                  </p>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="h-full bg-charcoal text-offwhite p-10 sm:p-12 relative grain overflow-hidden flex flex-col" data-testid="book-scheduler-placeholder">
              <div className="absolute inset-0 opacity-25 pointer-events-none" style={{ background: "radial-gradient(60% 70% at 80% 10%, rgba(198,161,91,0.5), transparent 65%)" }} />
              <div className="relative flex-1 flex flex-col items-center justify-center text-center">
                <span className="w-16 h-16 rounded-full border border-gold/40 flex items-center justify-center mb-7">
                  <CalendarClock size={26} className="text-gold" />
                </span>
                <h2 className="h-display font-semibold text-2xl sm:text-3xl leading-tight max-w-sm">
                  Live scheduling is being connected.
                </h2>
                <p className="text-white/75 text-[14px] mt-5 max-w-sm leading-relaxed">
                  This interface is ready for a scheduling provider (Calendly, Google Calendar or Microsoft Calendar).
                  Until it's switched on, the fastest way to book time is a consultation request — we'll reply with
                  proposed times.
                </p>
                <Link to="/contact" className="btn-gold mt-9 px-8 py-4" data-testid="book-request-button">
                  Request a time via the form <ArrowUpRight size={15} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
